import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import {
  useDigestConfigGet,
  useDigestConfigCreate,
  useDigestConfigUpdate,
} from "../hooks";
import { useReadUser } from "../services/api";
import type { DigestConfigCreate, DigestInterval } from "../types/digest";
import { CATALYST_EVENT_TYPES } from "../constants/catalystEventTypes";
import { getDigestTemplate } from "../digestTemplates";

import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Switch,
  Textarea,
  Typography,
} from "@mui/joy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import Loading from "../../../components/Loading";
import PageHeader from "../../../components/PageHeader";
import Page from "../../../components/Pages/Page";
import { FloatingChatWidget } from "../components/BKGAgent";

const DAYS_OF_WEEK = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

/**
 * Convert UTC HH:MM + day-of-week to local timezone.
 * Returns { time, dayShift } where dayShift is -1, 0, or +1.
 */
function utcToLocal(
  utcTime: string,
  utcDay: number | null,
): { time: string; day: number | null } {
  const [h, m] = utcTime.split(":").map(Number);
  const now = new Date();
  const utcDate = new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), h, m),
  );
  const localH = String(utcDate.getHours()).padStart(2, "0");
  const localM = String(utcDate.getMinutes()).padStart(2, "0");
  const dayShift = utcDate.getDate() - now.getDate();
  const adjustedDay = utcDay !== null ? (utcDay + dayShift + 7) % 7 : null;
  return { time: `${localH}:${localM}`, day: adjustedDay };
}

/**
 * Convert local HH:MM + day-of-week to UTC.
 */
function localToUtc(
  localTime: string,
  localDay: number | null,
): { time: string; day: number | null } {
  const [h, m] = localTime.split(":").map(Number);
  const now = new Date();
  const localDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    h,
    m,
  );
  const utcH = String(localDate.getUTCHours()).padStart(2, "0");
  const utcM = String(localDate.getUTCMinutes()).padStart(2, "0");
  const dayShift = localDate.getUTCDate() - localDate.getDate();
  const adjustedDay = localDay !== null ? (localDay + dayShift + 7) % 7 : null;
  return { time: `${utcH}:${utcM}`, day: adjustedDay };
}

const DigestConfigForm: React.FC = () => {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();
  const { config_id } = useParams<{ config_id?: string }>();
  const isEdit = !!config_id;
  const [searchParams] = useSearchParams();
  const template = isEdit
    ? undefined
    : getDigestTemplate(searchParams.get("template"));

  // A template only seeds the initial values; everything stays editable.
  const [name, setName] = useState(template?.name ?? "");
  const [prioritisationPrompt, setPrioritisationPrompt] = useState(
    template?.prioritisation_prompt ?? "",
  );
  const [eventTypeFilters, setEventTypeFilters] = useState<string[]>(
    template ? [...template.event_type_filters] : [],
  );
  const [interval, setInterval] = useState<DigestInterval | null>(
    template?.interval ?? null,
  );
  const [anchorTime, setAnchorTime] = useState(
    template?.anchor_time ?? "09:00",
  );
  const [anchorDay, setAnchorDay] = useState<number | null>(
    template?.anchor_day ?? null,
  );
  const [isPublic, setIsPublic] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(!!template);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState("");
  // Lookup of the creator's email for a template: saving waits for it.
  const [recipientLookup, setRecipientLookup] = useState<
    "idle" | "loading" | "failed"
  >(template ? "loading" : "idle");

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getConfig = useDigestConfigGet();
  const createConfig = useDigestConfigCreate();
  const updateConfig = useDigestConfigUpdate();
  const readUser = useReadUser();

  // Templates default to emailing the digest to the person creating it.
  const readUserRef = useRef(readUser);
  readUserRef.current = readUser;
  const templateId = template?.id;
  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    readUserRef
      .current()
      .then((user) => {
        if (cancelled) return;
        if (!user.email) {
          setRecipientLookup("failed");
          return;
        }
        setEmailRecipients((prev) => (prev.length === 0 ? [user.email] : prev));
        setRecipientLookup("idle");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error looking up your email address:", err);
        setRecipientLookup("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  useEffect(() => {
    if (!isEdit || !workspace?.id || !config_id) return;

    const load = async () => {
      try {
        setLoading(true);
        const config = await getConfig(workspace.id, config_id);
        setName(config.name);
        setPrioritisationPrompt(config.prioritisation_prompt);
        setEventTypeFilters(config.event_type_filters);
        setIsPublic(config.is_public ?? false);
        setEmailEnabled(config.email_enabled ?? false);
        setEmailRecipients(
          (config.email_recipients ?? [])
            .filter((r) => r.type === "email" && r.address)
            .map((r) => r.address!),
        );
        setInterval(config.interval);
        const local = utcToLocal(
          config.anchor_time || "09:00",
          config.anchor_day,
        );
        setAnchorTime(local.time);
        setAnchorDay(local.day);
      } catch (err) {
        console.error("Error loading digest config:", err);
        setError("Failed to load digest configuration.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isEdit, workspace, config_id, getConfig]);

  const handleSave = async () => {
    if (!workspace?.id) return;
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!prioritisationPrompt.trim()) {
      setError("Prioritisation prompt is required.");
      return;
    }
    if (emailEnabled && emailRecipients.length === 0) {
      setError("Add at least one email recipient, or turn off email delivery.");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const utc = localToUtc(anchorTime, anchorDay);
      const data: DigestConfigCreate = {
        name: name.trim(),
        prioritisation_prompt: prioritisationPrompt.trim(),
        event_type_filters: eventTypeFilters,
        interval: interval,
        anchor_time: interval ? utc.time : null,
        anchor_day: interval ? utc.day : null,
        is_public: isPublic,
        email_enabled: emailEnabled,
        email_recipients: emailRecipients.map((addr) => ({
          type: "email" as const,
          address: addr,
        })),
      };

      if (isEdit && config_id) {
        await updateConfig(workspace.id, config_id, data);
      } else {
        await createConfig(workspace.id, data);
      }

      navigate(`/workspace/${workspace.id}/bkg/catalyst-events/digests`);
    } catch (err) {
      console.error("Error saving digest config:", err);
      setError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Set sensible defaults when interval changes
  const handleIntervalChange = (newInterval: DigestInterval | null) => {
    setInterval(newInterval);
    if (newInterval === "weekly" || newInterval === "biweekly") {
      if (anchorDay === null || anchorDay > 6) setAnchorDay(0); // Monday
    } else if (newInterval === "monthly") {
      if (anchorDay === null || anchorDay < 1 || anchorDay > 28)
        setAnchorDay(1);
    } else {
      setAnchorDay(null);
    }
  };

  if (!workspace?.id) {
    return (
      <Page>
        <Alert color="danger" variant="soft">
          Workspace not specified.
        </Alert>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="plain"
            color="neutral"
            size="sm"
            startDecorator={<ArrowBackIcon />}
            onClick={() =>
              navigate(`/workspace/${workspace.id}/bkg/catalyst-events/digests`)
            }
          >
            Back
          </Button>
          <Typography level="h1">
            {isEdit ? "Edit Digest" : "New Digest"}
          </Typography>
        </Stack>
      </PageHeader>

      <Card sx={{ maxWidth: 700 }}>
        <CardContent>
          <Stack spacing={3}>
            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            {template && (
              <Alert color="primary" variant="soft">
                Started from the &ldquo;{template.name}&rdquo; template. Adjust
                anything below, then create the digest.
              </Alert>
            )}

            <FormControl required>
              <FormLabel>Name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Weekly Phase 3 Readouts"
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Prioritisation Prompt</FormLabel>
              <Textarea
                value={prioritisationPrompt}
                onChange={(e) => setPrioritisationPrompt(e.target.value)}
                minRows={4}
                maxRows={10}
                placeholder="Describe what types of events are most relevant to your business. For example: 'Prioritise Phase 3 clinical trial results and regulatory approvals for oncology companies in the Nordics.'"
              />
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                This prompt instructs the AI on how to rank and prioritise
                events in your digest.
              </Typography>
            </FormControl>

            <FormControl>
              <FormLabel>Event Type Filters</FormLabel>
              <Autocomplete
                multiple
                options={[...CATALYST_EVENT_TYPES] as string[]}
                value={eventTypeFilters}
                onChange={(_e, newValue) => setEventTypeFilters(newValue)}
                placeholder="All event types"
                renderTags={(tags, getTagProps) =>
                  tags.map((tag, index) => (
                    <Chip
                      size="sm"
                      variant="soft"
                      {...getTagProps({ index })}
                      key={tag}
                    >
                      {tag}
                    </Chip>
                  ))
                }
              />
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                Leave empty to include all event types.
              </Typography>
            </FormControl>

            <FormControl>
              <FormLabel>Generation Schedule</FormLabel>
              <Select
                value={interval || ""}
                onChange={(_e, newValue) =>
                  handleIntervalChange(
                    newValue ? (newValue as DigestInterval) : null,
                  )
                }
                placeholder="Manual only"
              >
                <Option value="">Manual only</Option>
                <Option value="daily">Daily</Option>
                <Option value="weekly">Weekly</Option>
                <Option value="biweekly">Bi-weekly</Option>
                <Option value="monthly">Monthly</Option>
              </Select>
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                {interval
                  ? "Each digest covers events since the previous digest was generated."
                  : "Generate digests manually using the 'Generate Now' button."}
              </Typography>
            </FormControl>

            {interval && (
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Time of Day</FormLabel>
                  <Input
                    type="time"
                    value={anchorTime}
                    onChange={(e) => setAnchorTime(e.target.value)}
                  />
                </FormControl>

                {(interval === "weekly" || interval === "biweekly") && (
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Day of Week</FormLabel>
                    <Select
                      value={anchorDay ?? 0}
                      onChange={(_e, newValue) =>
                        setAnchorDay(newValue as number)
                      }
                    >
                      {DAYS_OF_WEEK.map((d) => (
                        <Option key={d.value} value={d.value}>
                          {d.label}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {interval === "monthly" && (
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Day of Month</FormLabel>
                    <Select
                      value={anchorDay ?? 1}
                      onChange={(_e, newValue) =>
                        setAnchorDay(newValue as number)
                      }
                    >
                      {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                        <Option key={d} value={d}>
                          {d}
                        </Option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            )}

            <FormControl
              orientation="horizontal"
              sx={{ justifyContent: "space-between" }}
            >
              <div>
                <FormLabel>Public Digest</FormLabel>
                <Typography level="body-xs" color="neutral">
                  Make this digest visible to all workspace members.
                </Typography>
              </div>
              <Switch
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
            </FormControl>

            {/* Email Delivery */}
            <Card variant="outlined" sx={{ p: 2 }}>
              <FormControl
                orientation="horizontal"
                sx={{ justifyContent: "space-between", mb: 1.5 }}
              >
                <div>
                  <FormLabel>Email Delivery</FormLabel>
                  <Typography level="body-xs" color="neutral">
                    Send digest results by email after generation. Applies to
                    both scheduled and manual (&ldquo;Generate Now&rdquo;) runs.
                  </Typography>
                </div>
                <Switch
                  checked={emailEnabled}
                  onChange={(e) => setEmailEnabled(e.target.checked)}
                />
              </FormControl>

              {emailEnabled && (
                <Stack spacing={1}>
                  <FormLabel>Recipients</FormLabel>
                  {emailRecipients.map((addr, idx) => (
                    <Stack
                      key={idx}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Input size="sm" value={addr} readOnly sx={{ flex: 1 }} />
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() =>
                          setEmailRecipients((prev) =>
                            prev.filter((_, i) => i !== idx),
                          )
                        }
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Stack direction="row" spacing={1}>
                    <Input
                      size="sm"
                      placeholder="email@example.com"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && newRecipient.includes("@")) {
                          setEmailRecipients((prev) => [
                            ...prev,
                            newRecipient.trim(),
                          ]);
                          setNewRecipient("");
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      size="sm"
                      variant="outlined"
                      disabled={!newRecipient.includes("@")}
                      onClick={() => {
                        setEmailRecipients((prev) => [
                          ...prev,
                          newRecipient.trim(),
                        ]);
                        setNewRecipient("");
                      }}
                    >
                      Add
                    </Button>
                  </Stack>
                  {recipientLookup === "failed" &&
                    emailRecipients.length === 0 && (
                      <Typography level="body-xs" color="danger">
                        Couldn&rsquo;t look up your email address. Add a
                        recipient above.
                      </Typography>
                    )}
                  {recipientLookup !== "failed" &&
                    emailRecipients.length === 0 && (
                      <Typography level="body-xs" color="neutral">
                        Add at least one recipient to enable email delivery.
                      </Typography>
                    )}
                </Stack>
              )}
            </Card>

            <Stack direction="row" spacing={2}>
              <Button
                onClick={handleSave}
                loading={saving}
                disabled={emailEnabled && recipientLookup === "loading"}
              >
                {isEdit ? "Save Changes" : "Create Digest"}
              </Button>
              <Button
                variant="outlined"
                color="neutral"
                onClick={() =>
                  navigate(
                    `/workspace/${workspace.id}/bkg/catalyst-events/digests`,
                  )
                }
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <FloatingChatWidget workspaceId={workspace?.id ?? ""} />
    </Page>
  );
};

export default DigestConfigForm;
